/**
 * 운영 콘솔 무대 꾸밈 미리보기용(2026-10-08) — 마이크로비트 장면의 기본 템플릿(처음 상태)과 그 템플릿이 무대 판에서 차지하는 자리.
 * 꾸밈 편집기가 미리보기에 길 · 칸 · 광장을 함께 그리고, 그 자리에 놓인 소품 · 손님 · 모델을 '겹쳐요'로 알린다(그리기는 막지 않는다).
 * 좌표: m · 무대 가운데 원점 · 카메라는 +z(렌더러 mbTemplates 와 같은 치수).
 */
import type { SceneId } from "./scenes";
import { initialFaceCourse, initialHeartbeat, initialTrack } from "./scenes";
import { initialLessons } from "./lessons";
import { initialPlaza } from "./radio";
import { templateOf } from "./stageTemplate";
import type { MbTemplate } from "./stageTemplate";
import { stageDecorOf } from "@picnic-field/renderer/stageDecor";
import type { StageDecorData } from "@picnic-field/renderer/stageDecor";
import { stageTheme } from "@picnic-field/renderer/stageScenes";
import { themeOfScene } from "./stage3d";

/** 미리보기 광장 친구(자리만 — 별명은 보기용) */
const SAMPLE_MEMBERS = ["친구1", "친구2", "친구3", "친구4", "친구5", "친구6"].map((name) => ({
  name,
}));

/** 장면을 처음 연 모습의 템플릿(진행 0 · 광장은 보기용 친구 여섯) */
export function defaultTemplateOf(scene: SceneId): MbTemplate {
  const plaza = { ...initialPlaza(), partners: ["친구1", "친구3"] };
  return templateOf({
    scene,
    lessons: initialLessons(),
    track: initialTrack(),
    course: initialFaceCourse(),
    heart: initialHeartbeat(),
    sensors: {},
    plaza,
    members: SAMPLE_MEMBERS,
  });
}

export type Footprint =
  | {
      readonly kind: "rect";
      readonly x0: number;
      readonly x1: number;
      readonly z0: number;
      readonly z1: number;
    }
  | { readonly kind: "circle"; readonly cx: number; readonly cz: number; readonly r: number };

/** 템플릿이 차지하는 무대 판 자리(받침대 장면은 없음 — 받침대 둘레 비우기가 대신한다) */
export function footprintOf(t: MbTemplate): Footprint | null {
  // 렌더러 mbTemplates 치수: 달리기 길 1.3m × 0.2m(+ 깃발 줄) · 격자 n × 0.16m · 광장 반원 반지름 ≤ 0.84m(가운데 z 0.15)
  if (t.kind === "run") return { kind: "rect", x0: -0.72, x1: 0.72, z0: -0.2, z1: 0.12 };
  if (t.kind === "grid") {
    const h = ((t.n - 1) * 0.16) / 2 + 0.08;
    return { kind: "rect", x0: -h, x1: h, z0: -h, z1: h };
  }
  if (t.kind === "plaza") return { kind: "circle", cx: 0, cz: 0.15, r: 0.95 };
  return null;
}

/** 이 자리가 템플릿과 겹치나(pad = 물체 반지름 m) */
export function inFootprint(f: Footprint | null, x: number, z: number, pad = 0.05): boolean {
  if (!f) return false;
  if (f.kind === "rect")
    return x >= f.x0 - pad && x <= f.x1 + pad && z >= f.z0 - pad && z <= f.z1 + pad;
  return Math.hypot(x - f.cx, z - f.cz) <= f.r + pad;
}

/**
 * 운영자가 꾸미지 않은 템플릿 장면의 기본 꾸밈 — 기본 무대 장면(stageTheme)에서 템플릿 자리(길 · 칸 · 광장)에 놓인 소품 · 손님을 뺀 꾸밈 데이터.
 * 받침대 장면이거나 뺄 것이 없으면 null(기본 장면 그대로). 학생 3D 무대와 무대 꾸밈 편집기(처음 꾸밈)가 같은 것을 쓴다.
 */
export function cleanDefaultDecor(scene: SceneId): StageDecorData | null {
  const f = footprintOf(defaultTemplateOf(scene));
  if (!f) return null;
  const base = stageDecorOf(stageTheme(themeOfScene(scene)));
  const props = (base.props ?? []).filter(
    (p) => !inFootprint(f, p.at[0], p.at[1], 0.12 * (p.scale ?? 1)),
  );
  const figures = (base.figures ?? []).filter(
    (g) => g.at[1] >= 0.4 || !inFootprint(f, g.at[0], g.at[2], g.size * 0.4),
  );
  if (props.length === (base.props ?? []).length && figures.length === (base.figures ?? []).length)
    return null;
  return { ...base, props, figures };
}
